import NetInfo from '@react-native-community/netinfo';
import { journeyRepository } from './database/JourneyRepository';
import { journeyApi } from './api/journeyApi';
import { offlineSyncManager } from './sync/OfflineSyncManager';
import { logger } from '../utils/logger';
import type {
  Journey,
  JourneyWeek,
  JourneyActivity,
  ActivityProgress,
  CreateJourneyInput,
  CompletionInput,
  JourneyStats,
  SkillMastery,
} from '@coffeeplexity/shared';

/**
 * Offline-aware Journey Service
 *
 * Provides a unified interface for journey operations that works both
 * online and offline. Automatically handles caching, optimistic updates,
 * and background synchronization.
 */
export class OfflineJourneyService {
  private static instance: OfflineJourneyService;

  private constructor() {}

  public static getInstance(): OfflineJourneyService {
    if (!OfflineJourneyService.instance) {
      OfflineJourneyService.instance = new OfflineJourneyService();
    }
    return OfflineJourneyService.instance;
  }

  /**
   * Check if device is online
   */
  private async isOnline(): Promise<boolean> {
    const netInfo = await NetInfo.fetch();
    return netInfo.isConnected ?? false;
  }

  /**
   * Generate operation ID for idempotency
   */
  private generateOperationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // ============================================================================
  // JOURNEY OPERATIONS
  // ============================================================================

  /**
   * Get user's journeys (offline-first)
   */
  async getMyJourneys(userId: string): Promise<Journey[]> {
    try {
      // Always try local first for better performance
      const localJourneys = await journeyRepository.getJourneys(userId);

      if (await this.isOnline()) {
        try {
          // Fetch from API and update local cache
          const apiJourneys = await journeyApi.getMyJourneys();

          // Save to local database (without sync flag since it's from server)
          for (const journey of apiJourneys) {
            await journeyRepository.saveJourney(journey, false);
          }

          return apiJourneys;
        } catch (error) {
          logger.warn(
            'Failed to fetch journeys from API, using local cache:',
            error
          );
          return localJourneys;
        }
      }

      return localJourneys;
    } catch (error) {
      logger.error('Failed to get journeys:', error);
      throw error;
    }
  }

  /**
   * Get specific journey (offline-first)
   */
  async getJourney(journeyId: string): Promise<Journey | null> {
    try {
      // Try local first
      const localJourney = await journeyRepository.getJourney(journeyId);

      if (await this.isOnline()) {
        try {
          // Fetch from API and update local cache
          const apiJourney = await journeyApi.getJourney(journeyId);
          await journeyRepository.saveJourney(apiJourney, false);
          return apiJourney;
        } catch (error) {
          logger.warn(
            'Failed to fetch journey from API, using local cache:',
            error
          );
          return localJourney;
        }
      }

      return localJourney;
    } catch (error) {
      logger.error('Failed to get journey:', error);
      throw error;
    }
  }

  /**
   * Get journey week (offline-first)
   */
  async getJourneyWeek(
    journeyId: string,
    weekNumber: number
  ): Promise<JourneyWeek | null> {
    try {
      // For weeks, we get them as part of the journey
      const journey = await this.getJourney(journeyId);
      if (!journey) {
        return null;
      }

      return (
        journey.weeks.find(week => week.week_number === weekNumber) || null
      );
    } catch (error) {
      logger.error('Failed to get journey week:', error);
      throw error;
    }
  }

  /**
   * Get journey activity (offline-first)
   */
  async getJourneyActivity(
    activityId: string
  ): Promise<JourneyActivity | null> {
    try {
      if (await this.isOnline()) {
        try {
          // Fetch from API
          const apiActivity = await journeyApi.getJourneyActivity(activityId);
          return apiActivity;
        } catch (error) {
          logger.warn('Failed to fetch activity from API:', error);
        }
      }

      // Fallback: search in local journeys
      // This is less efficient but works offline
      const userId = 'current_user'; // Should be from auth context
      const journeys = await journeyRepository.getJourneys(userId);

      for (const journey of journeys) {
        for (const week of journey.weeks) {
          const activity = week.activities.find(a => a.id === activityId);
          if (activity) {
            return activity;
          }
        }
      }

      return null;
    } catch (error) {
      logger.error('Failed to get journey activity:', error);
      throw error;
    }
  }

  /**
   * Create new journey (online-only, cached locally)
   */
  async createJourney(input: CreateJourneyInput): Promise<Journey> {
    if (!(await this.isOnline())) {
      throw new Error('Creating journeys requires internet connection');
    }

    try {
      const journey = await journeyApi.createJourney(input);

      // Cache locally
      await journeyRepository.saveJourney(journey, false);

      return journey;
    } catch (error) {
      logger.error('Failed to create journey:', error);
      throw error;
    }
  }

  // ============================================================================
  // ACTIVITY PROGRESS OPERATIONS
  // ============================================================================

  /**
   * Complete activity (optimistic update with sync)
   */
  async completeActivity(
    activityId: string,
    payload: CompletionInput,
    userId: string = 'current_user'
  ): Promise<ActivityProgress> {
    const operationId = this.generateOperationId();

    try {
      // Create optimistic progress record
      const optimisticProgress: ActivityProgress = {
        id: `${activityId}-progress`,
        user_id: userId,
        activity_id: activityId,
        status: 'completed',
        progress_percentage: 100,
        attempts: 1,
        time_spent_minutes: payload.time_spent_minutes,
        success_rate: payload.success_rate,
        engagement_score: payload.engagement_score,
        difficulty_rating: payload.difficulty_rating,
        started_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        last_accessed_at: new Date().toISOString(),
      };

      // Save locally with sync flag
      await journeyRepository.saveActivityProgress(
        optimisticProgress,
        userId,
        true, // needs sync
        operationId
      );

      // Trigger background sync
      offlineSyncManager.triggerSync();

      return optimisticProgress;
    } catch (error) {
      logger.error('Failed to complete activity:', error);
      throw error;
    }
  }

  /**
   * Update activity progress (optimistic update with sync)
   */
  async updateActivityProgress(
    activityId: string,
    progressPercentage: number,
    userId: string = 'current_user'
  ): Promise<ActivityProgress> {
    const operationId = this.generateOperationId();

    try {
      // Get existing progress or create new
      let progress = await journeyRepository.getActivityProgress(
        userId,
        activityId
      );

      if (!progress) {
        progress = {
          id: `${activityId}-progress`,
          user_id: userId,
          activity_id: activityId,
          status: 'in_progress',
          progress_percentage: 0,
          attempts: 0,
          time_spent_minutes: 0,
          started_at: new Date().toISOString(),
          last_accessed_at: new Date().toISOString(),
        };
      }

      // Update progress
      progress.progress_percentage = progressPercentage;
      progress.last_accessed_at = new Date().toISOString();

      if (progressPercentage > 0 && !progress.started_at) {
        progress.started_at = new Date().toISOString();
        progress.status = 'in_progress';
      }

      // Save locally with sync flag
      await journeyRepository.saveActivityProgress(
        progress,
        userId,
        true, // needs sync
        operationId
      );

      // Trigger background sync
      offlineSyncManager.triggerSync();

      return progress;
    } catch (error) {
      logger.error('Failed to update activity progress:', error);
      throw error;
    }
  }

  /**
   * Rate activity (optimistic update with sync)
   */
  async rateActivity(
    activityId: string,
    rating: number,
    feedback?: string,
    userId: string = 'current_user'
  ): Promise<void> {
    try {
      if (await this.isOnline()) {
        try {
          await journeyApi.rateActivity(activityId, rating, feedback);
          return;
        } catch (error) {
          logger.warn(
            'Failed to rate activity online, will sync later:',
            error
          );
        }
      }

      // Store rating locally for later sync
      // This would require extending the schema to store ratings separately
      logger.log(`Rating stored locally: ${activityId} = ${rating}`);

      // Trigger background sync
      offlineSyncManager.triggerSync();
    } catch (error) {
      logger.error('Failed to rate activity:', error);
      throw error;
    }
  }

  // ============================================================================
  // READ-ONLY OPERATIONS (Online with fallback)
  // ============================================================================

  /**
   * Adapt journey (online-only)
   */
  async adaptJourney(journeyId: string): Promise<Journey> {
    if (!(await this.isOnline())) {
      throw new Error('Journey adaptation requires internet connection');
    }

    try {
      const journey = await journeyApi.adaptJourney(journeyId);

      // Update local cache
      await journeyRepository.saveJourney(journey, false);

      return journey;
    } catch (error) {
      logger.error('Failed to adapt journey:', error);
      throw error;
    }
  }

  /**
   * Get journey stats (online with local fallback)
   */
  async getJourneyStats(journeyId: string): Promise<JourneyStats | null> {
    try {
      if (await this.isOnline()) {
        return await journeyApi.getJourneyStats(journeyId);
      }

      // Could implement local stats calculation here
      logger.warn('Journey stats not available offline');
      return null;
    } catch (error) {
      logger.error('Failed to get journey stats:', error);
      return null;
    }
  }

  /**
   * Get skill mastery (online-only)
   */
  async getSkillMastery(journeyId: string): Promise<SkillMastery[]> {
    if (!(await this.isOnline())) {
      throw new Error('Skill mastery requires internet connection');
    }

    try {
      return await journeyApi.getSkillMastery(journeyId);
    } catch (error) {
      logger.error('Failed to get skill mastery:', error);
      throw error;
    }
  }

  /**
   * Get recommendations (online-only)
   */
  async getRecommendations(
    journeyId: string,
    limit: number = 5
  ): Promise<JourneyActivity[]> {
    if (!(await this.isOnline())) {
      throw new Error('Recommendations require internet connection');
    }

    try {
      return await journeyApi.getRecommendations(journeyId, limit);
    } catch (error) {
      logger.error('Failed to get recommendations:', error);
      throw error;
    }
  }

  /**
   * Update journey status (online with local cache)
   */
  async updateJourneyStatus(
    journeyId: string,
    status: 'active' | 'paused'
  ): Promise<Journey> {
    if (!(await this.isOnline())) {
      throw new Error('Updating journey status requires internet connection');
    }

    try {
      const journey = await journeyApi.updateJourneyStatus(journeyId, status);

      // Update local cache
      await journeyRepository.saveJourney(journey, false);

      return journey;
    } catch (error) {
      logger.error('Failed to update journey status:', error);
      throw error;
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get offline status and sync info
   */
  async getOfflineStatus(): Promise<{
    isOnline: boolean;
    hasCachedData: boolean;
    pendingSync: number;
    lastSyncAt: string | null;
  }> {
    const syncStatus = await offlineSyncManager.getSyncStatus();
    const userId = 'current_user'; // Should be from auth context
    const journeys = await journeyRepository.getJourneys(userId);

    return {
      isOnline: syncStatus.isOnline,
      hasCachedData: journeys.length > 0,
      pendingSync: syncStatus.pendingOperations,
      lastSyncAt: syncStatus.lastSyncAt,
    };
  }

  /**
   * Force sync all pending operations
   */
  async forceSyncAll(): Promise<void> {
    await offlineSyncManager.forceSyncAll();
  }

  /**
   * Clear all offline data
   */
  async clearOfflineData(): Promise<void> {
    await journeyRepository.clearAllData();
    await offlineSyncManager.clearSyncData();
  }
}

// Export singleton instance
export const offlineJourneyService = OfflineJourneyService.getInstance();
